import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, sendLoginCode, verifyLoginCode, sendSmsLoginCode, verifySmsLoginCode, getProfile } from '../lib/supabase'

export default function Login() {
  const [mode, setMode] = useState('password') // 'password' | 'code' | 'sms'
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  async function routeAfterLogin() {
    const profile = await getProfile()
    if (profile?.role === 'owner') navigate('/portal')
    else navigate('/')
  }

  // ── Password sign-in ──────────────────────────────────────────
  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signIn(email, password)
      await routeAfterLogin()
    } catch (err) {
      setError(err.message || 'Invalid credentials')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 1: request a code ────────────────────────────────────
  async function handleSendCode(e) {
    e.preventDefault()
    setError(''); setInfo('')
    setLoading(true)
    try {
      await sendLoginCode(email)
      setCodeSent(true)
      setInfo(`We emailed a login code to ${email.trim()}. It expires in a few minutes.`)
    } catch (err) {
      const msg = /signups? not allowed|otp/i.test(err.message || '')
        ? 'No account found for that email. Ask your admin to create your account first.'
        : (err.message || 'Could not send a code. Please try again.')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2: verify the code ───────────────────────────────────
  async function handleVerifyCode(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await verifyLoginCode(email, code)
      await routeAfterLogin()
    } catch (err) {
      setError(/expired|invalid|token/i.test(err.message || '')
        ? 'That code is invalid or expired. Request a new one.'
        : (err.message || 'Could not verify the code.'))
    } finally {
      setLoading(false)
    }
  }

  // ── Text-message code (Quo) ───────────────────────────────────
  const phoneDigits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')

  function formatPhone(raw) {
    const d = raw.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').slice(0, 10)
    if (d.length < 4) return d
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  }

  async function handleSendSms(e) {
    e.preventDefault()
    setError(''); setInfo('')
    setLoading(true)
    try {
      await sendSmsLoginCode(phoneDigits)
      setCodeSent(true)
      setInfo(`We texted a login code to ${formatPhone(phoneDigits)}. It expires in 10 minutes.`)
    } catch (err) {
      setError(err.message || 'Could not send a code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifySms(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await verifySmsLoginCode(phoneDigits, code)
      await routeAfterLogin()
    } catch (err) {
      setError(err.message || 'Could not verify the code.')
    } finally {
      setLoading(false)
    }
  }

  function switchMode(next) {
    setMode(next)
    setError(''); setInfo('')
    setCode(''); setPassword('')
    setCodeSent(false)
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <img src="/logo.png" alt="Peacock Rentals" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'invert(0)' }} />
          <div className="auth-logo" style={{ marginBottom: 0 }}>Peacock Rentals</div>
        </div>
        <div className="auth-sub">Management Portal</div>

        {error && <div className="alert alert-error">{error}</div>}
        {info && <div className="alert alert-success" style={{ fontSize: 14 }}>{info}</div>}

        {/* ── PASSWORD MODE ── */}
        {mode === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={password}
                onChange={e => setPassword(e.target.value)} required />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => switchMode('code')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Email me a login code instead
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => switchMode('sms')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Text me a login code instead
              </button>
            </div>
          </form>
        )}

        {/* ── CODE MODE ── */}
        {mode === 'code' && !codeSent && (
          <form onSubmit={handleSendCode}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus
                placeholder="you@example.com" />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
              {loading ? 'Sending…' : 'Email me a code'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => switchMode('password')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Use a password instead
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => switchMode('sms')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Text me a code instead
              </button>
            </div>
          </form>
        )}

        {mode === 'code' && codeSent && (
          <form onSubmit={handleVerifyCode}>
            <div className="form-group">
              <label className="form-label">Enter the code from your email</label>
              <input className="form-input" type="text" inputMode="numeric" autoComplete="one-time-code"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                required autoFocus maxLength={10}
                style={{ letterSpacing: 6, textAlign: 'center', fontSize: 20 }} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading || code.length < 6}>
              {loading ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, fontSize: 14 }}>
              <button type="button" onClick={handleSendCode} disabled={loading}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', textDecoration: 'underline' }}>
                Resend code
              </button>
              <button type="button" onClick={() => switchMode('code')}
                style={{ background: 'none', border: 'none', color: 'var(--text3, #888)', cursor: 'pointer', textDecoration: 'underline' }}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {/* ── TEXT (SMS) MODE ── */}
        {mode === 'sms' && !codeSent && (
          <form onSubmit={handleSendSms}>
            <div className="form-group">
              <label className="form-label">Mobile number</label>
              <input className="form-input" type="tel" inputMode="tel" autoComplete="tel-national"
                value={phone} onChange={e => setPhone(formatPhone(e.target.value))}
                required autoFocus placeholder="(714) 555-1234" />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading || phoneDigits.length !== 10}>
              {loading ? 'Sending…' : 'Text me a code'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button type="button" onClick={() => switchMode('code')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Use email instead
              </button>
              <button type="button" onClick={() => switchMode('password')}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>
                Use a password
              </button>
            </div>
          </form>
        )}

        {mode === 'sms' && codeSent && (
          <form onSubmit={handleVerifySms}>
            <div className="form-group">
              <label className="form-label">Enter the code we texted you</label>
              <input className="form-input" type="text" inputMode="numeric" autoComplete="one-time-code"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required autoFocus maxLength={6}
                style={{ letterSpacing: 6, textAlign: 'center', fontSize: 20 }} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading || code.length < 6}>
              {loading ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, fontSize: 14 }}>
              <button type="button" onClick={handleSendSms} disabled={loading}
                style={{ background: 'none', border: 'none', color: 'var(--gold, #a861eb)', cursor: 'pointer', textDecoration: 'underline' }}>
                Resend code
              </button>
              <button type="button" onClick={() => switchMode('sms')}
                style={{ background: 'none', border: 'none', color: 'var(--text3, #888)', cursor: 'pointer', textDecoration: 'underline' }}>
                Use a different number
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
